import React, { useEffect, useRef, useState } from "react";
import FullScreenPanel from "../../ui/FullScreenPanel.jsx";
import { phoneUi } from "../../ui/phoneUi";
import { shrinkPhoto } from "../../ui/shrinkPhoto";
import { JOB_PRIORITY_OPTIONS, JOB_STATUS_OPTIONS, canEditDueDate, loadShipParts } from "../../../features/jobs/jobsData";
import { patchJob, preShip, preShipItems, remainingToShip, uploadJobDocument } from "../../../features/jobs/jobActions";
import { clipCount, clipTimeCT, listTaskClips, loadClipUrls, taskCameraCount } from "../../../features/jobs/taskVideo";
import { CAMERA_NAMES } from "../../../utils/cameraRelay";

// The phone job detail's full-screen panels. Layout only: every read and action is
// features/jobs/ (the desktop Jobs pages call the same functions).

const dateOnly = (v) => (v ? String(v).split("T")[0] : "");

/** Edit status, priority, due date and notes (no PIN, as on the desktop). Sends only what changed. */
export function JobEditPanel({ job, onClose, onSaved }) {
  const [status, setStatus] = useState(job.status || "Pending");
  const [priority, setPriority] = useState(String(job.priority_level || 2));
  const [due, setDue] = useState(dateOnly(job.due_date));
  const [notes, setNotes] = useState(job.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dueEditable = canEditDueDate(job);
  // A job set by hand to a status this list doesn't hold (e.g. Cancelled) keeps it as an option.
  const statuses = JOB_STATUS_OPTIONS.includes(job.status) || !job.status ? JOB_STATUS_OPTIONS : [job.status, ...JOB_STATUS_OPTIONS];

  async function save() {
    const patch = {};
    if (status !== job.status) patch.status = status;
    if (Number(priority) !== Number(job.priority_level)) patch.priority_level = Number(priority);
    if (dueEditable && due !== dateOnly(job.due_date)) patch.due_date = due || null;
    if (notes !== (job.notes || "")) patch.notes = notes;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await patchJob(job.job_id, patch);
      onSaved(patch);
    } catch (err) {
      setError(err.message || "The job was not saved.");
      setSaving(false);
    }
  }

  return (
    <FullScreenPanel
      open
      title={`Edit Job #${job.master_job_num || job.job_id}`}
      onClose={onClose}
      testId="job-edit-panel"
      footer={
        <div className="jq-two-buttons">
          <button type="button" className="btn btn-outline-secondary btn-lg" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary btn-lg" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      }
    >
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      <div className="jq-filter-field">
        <label htmlFor="job-status">Status</label>
        <select id="job-status" className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="job-priority">Priority</label>
        <select id="job-priority" className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
          {JOB_PRIORITY_OPTIONS.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
        </select>
      </div>
      <div className="jq-filter-field">
        <label htmlFor="job-due">Due date</label>
        <input id="job-due" type="date" className="form-control" value={due} onChange={(e) => setDue(e.target.value)} disabled={!dueEditable} />
        {!dueEditable && <div className="jq-muted mt-1">A {String(job.status).toLowerCase()} job's due date doesn't change.</div>}
      </div>
      <div className="jq-filter-field">
        <label htmlFor="job-notes">Notes</label>
        <textarea id="job-notes" className="form-control" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </FullScreenPanel>
  );
}

/** Pre-ship: a quantity per part to ship now, before the job finishes (PIN). */
export function PreShipPanel({ job, onClose, onDone }) {
  const [parts, setParts] = useState([]);
  const [qtys, setQtys] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    loadShipParts(job.job_id)
      .then((data) => { if (alive) setParts(data); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [job.job_id]);

  async function submit() {
    setError("");
    const plan = preShipItems(parts, qtys);
    if (plan.error) {
      setError(plan.error);
      return;
    }
    const result = await preShip({ jobId: job.job_id, items: plan.items, ui: phoneUi, setBusy });
    if (result.error) setError(result.error);
    if (result.done) onDone();
  }

  return (
    <FullScreenPanel
      open
      title={`Pre-ship Job #${job.master_job_num || job.job_id}`}
      onClose={onClose}
      testId="pre-ship-panel"
      footer={
        <button type="button" className="btn btn-primary btn-lg w-100" onClick={submit} disabled={busy || loading}>
          {busy ? "Pre-shipping…" : "Pre-ship → Shipping queue"}
        </button>
      }
    >
      <p className="jq-muted">
        Ship a quantity now, before the job finishes. It goes to the shipping queue; the rest ships when production
        completes.
      </p>
      {error && <div className="jq-note jq-note-danger" role="alert">{error}</div>}
      {loading && <div className="jq-list-state" role="status">Loading parts…</div>}
      {parts.map((p) => {
        const left = remainingToShip(p);
        return (
          <div key={p.part_id} className="jq-ship-row">
            <div className="fw-semibold">{p.product_id}</div>
            {p.description && <div className="jq-muted">{p.description}</div>}
            <div className="jq-part-row-meta">{p.quantity_ordered} ordered · {p.quantity_shipped || 0} shipped · {left} left</div>
            {p.component_of_assembly ? (
              <div className="jq-muted fst-italic mt-1">Ships with {p.component_of_assembly}</div>
            ) : (
              <input
                type="number"
                inputMode="numeric"
                min="0"
                max={left}
                className="form-control mt-2"
                aria-label={`Pre-ship quantity for ${p.product_id}`}
                value={qtys[p.part_id] || ""}
                disabled={left <= 0}
                onChange={(e) => setQtys((q) => ({ ...q, [p.part_id]: e.target.value }))}
              />
            )}
          </div>
        );
      })}
    </FullScreenPanel>
  );
}

const photoName = (now = new Date()) =>
  `Photo ${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
const fileBaseName = (name) => String(name || "File").replace(/\.[^.]+$/, "");

/** Name and attach a picked photo / file to the job (PIN). */
export function AddFilePanel({ job, file, isPhoto, onClose, onDone }) {
  const [name, setName] = useState(() => (isPhoto ? photoName() : fileBaseName(file && file.name)));
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview] = useState(() => (isPhoto && file && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null));
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  async function upload() {
    // A full-size photo can pass the API's upload limit: send it at most 2048 px (shrinkPhoto).
    setBusy(true);
    const toSend = /^image\//.test((file && file.type) || "") ? await shrinkPhoto(file) : file;
    setBusy(false);
    const result = await uploadJobDocument({ jobId: job.job_id, file: toSend, displayName: name, description, ui: phoneUi, setBusy });
    if (result.reload) onDone();
  }

  return (
    <FullScreenPanel
      open
      title={isPhoto ? "Add photo" : "Add file"}
      onClose={onClose}
      testId="add-file-panel"
      footer={
        <button type="button" className="btn btn-primary btn-lg w-100" onClick={upload} disabled={busy}>
          {busy ? "Uploading…" : `Add to Job #${job.master_job_num || job.job_id}`}
        </button>
      }
    >
      {preview && <img src={preview} alt="" className="w-100 mb-3" style={{ borderRadius: 10, maxHeight: "40vh", objectFit: "contain" }} />}
      {!isPhoto && file && <div className="jq-note jq-note-info">{file.name}</div>}
      <div className="jq-filter-field">
        <label htmlFor="file-name">Name</label>
        <input id="file-name" className="form-control" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      </div>
      <div className="jq-filter-field">
        <label htmlFor="file-description">Description (optional)</label>
        <input id="file-description" className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} autoComplete="off" />
      </div>
    </FullScreenPanel>
  );
}

/** A finished task's camera footage: every camera's clip side by side, clip by clip. */
export function TaskVideoPanel({ task, cameraConfig, onClose }) {
  const [clips, setClips] = useState(null);
  const [idx, setIdx] = useState(0);
  const [urls, setUrls] = useState({});
  const [status, setStatus] = useState("loading"); // loading | ready | none | failed
  const urlsRef = useRef({});

  const revoke = () => {
    Object.values(urlsRef.current).forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* gone */ } });
    urlsRef.current = {};
  };
  useEffect(() => revoke, []);

  useEffect(() => {
    let alive = true;
    listTaskClips(task, taskCameraCount(task, cameraConfig)).then((all) => {
      if (!alive) return;
      setClips(all);
      setStatus(clipCount(all) ? "ready" : "none");
    });
    return () => { alive = false; };
  }, [task, cameraConfig]);

  useEffect(() => {
    if (!clips || !clipCount(clips)) return undefined;
    let alive = true;
    setStatus("loading");
    loadClipUrls(clips, idx, task.workstation).then((next) => {
      if (!alive) {
        Object.values(next).forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      revoke();
      urlsRef.current = next;
      setUrls(next);
      setStatus(Object.keys(next).length ? "ready" : "failed");
    });
    return () => { alive = false; };
  }, [clips, idx, task.workstation]);

  const total = clipCount(clips || {});
  const first = clips && clips[0] ? clips[0][idx] : null;

  return (
    <FullScreenPanel
      open
      title={`${task.workstation} · task ${task.task_id}`}
      onClose={onClose}
      testId="task-video-panel"
      footer={total > 1 ? (
        <div className="jq-clip-nav">
          <button type="button" className="btn btn-outline-secondary" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} aria-label="Previous clip">
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <span>Clip {idx + 1} of {total}{first ? ` · ${clipTimeCT(first)}` : ""}</span>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} disabled={idx >= total - 1} aria-label="Next clip">
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    >
      {status === "loading" && <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Loading video…</div>}
      {status === "none" && <div className="jq-list-state">No recordings found for this task's time range.</div>}
      {status === "failed" && <div className="jq-note jq-note-danger">The video didn't load. The camera Pi may be busy; try again.</div>}
      {status === "ready" && (
        <div className="jq-video-grid">
          {Object.entries(urls).map(([cam, url]) => {
            const name = (CAMERA_NAMES[task.workstation] || [])[Number(cam)] || `Camera ${Number(cam) + 1}`;
            return (
              <div key={`${cam}-${idx}`}>
                <div className="jq-muted mb-1">{name}</div>
                <video
                  src={url}
                  controls
                  playsInline
                  autoPlay={cam === Object.keys(urls)[0]}
                  muted
                  onEnded={cam === Object.keys(urls)[0] && idx < total - 1 ? () => setIdx(idx + 1) : undefined}
                  aria-label={`${task.workstation} ${name}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </FullScreenPanel>
  );
}
